/*
 * https://github.com/okta/okta-react/issues/178
 * https://github.com/okta/okta-react/blob/master/samples/routing/react-router-dom-v6/src/components/SecureRoute.tsx
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import React, { useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { useOktaAuth } from "@okta/okta-react";
import { toRelativeUrl } from "@okta/okta-auth-js";
import { Outlet, useNavigate } from "react-router-dom";

import { useCognitoAuth } from "./CognitoAuthProvider";

interface RequiredAuthProps {
  useCognito: boolean;
}

export const RequiredAuth: React.FC<RequiredAuthProps> = ({ useCognito }) => {
  const navigate = useNavigate();

  // Cognito auth flow
  if (useCognito) {
    const { isAuthenticated, isLoading } = useCognitoAuth();

    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        navigate("/login");
      }

      if (isAuthenticated && window.top) {
        window.top.postMessage("isAuthenticated", "*");
      }
    }, [isLoading, isAuthenticated, navigate]);

    if (isLoading || !isAuthenticated) {
      return (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <CircularProgress size={40} />
        </div>
      );
    }

    return <Outlet />;
  }

  // Okta auth flow (legacy)
  const { oktaAuth, authState } = useOktaAuth();

  useEffect(() => {
    if (!authState) {
      return;
    }

    if (!authState?.isAuthenticated) {
      const originalUri = toRelativeUrl(
        window.location.href,
        window.location.origin
      );
      oktaAuth.setOriginalUri(originalUri);
      oktaAuth.signInWithRedirect();
    }
    if (authState?.isAuthenticated && window.top) {
      window.top.postMessage("isAuthenticated", "*");
    }
  }, [oktaAuth, !!authState, authState?.isAuthenticated]);

  if (!authState || !authState?.isAuthenticated) {
    return (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <CircularProgress size={40} />
      </div>
    );
  }

  return <Outlet />;
};
